import { IProjectData } from '@droid/project-interface'
import { javaLib } from './javaLibDataMap'
import { Log } from '@fanfanlo'

export type LibPathKey = keyof typeof javaLib

const fileLog = new Log(false, 'extractDependencies_f')
fileLog.pause = true;
fileLog.childrenPaused = true;
/**
 * 从 IProjectData 中提取所有使用的 Trigger 和 Action 的 type
 *
 * @param projectData - Project 数据对象
 * @returns 使用的类型集合
 *
 * @example
 * const types = extractUsedTypes(projectData)
 * // types: Set { "fanfanlo/triggers/time/timeout", "fanfanlo/actions/app/openApp" }
 */
export function extractUsedTypes(projectData: IProjectData): Set<string> {
    fileLog.log('[extractUsedTypes] 开始提取类型')
    const types = new Set<string>()

    // 遍历所有 TriggerAction
    fileLog.log('[extractUsedTypes] triggerActionGroup.children 长度:', projectData.triggerActionGroup.children.length)
    for (let i = 0; i < projectData.triggerActionGroup.children.length; i++) {
        const triggerAction = projectData.triggerActionGroup.children[i]
        fileLog.log('[extractUsedTypes] 处理 TriggerAction', i, triggerAction.id)

        // 提取 Triggers
        fileLog.log('[extractUsedTypes] triggerGroup.children 长度:', triggerAction.triggerGroup.children.length)
        for (let j = 0; j < triggerAction.triggerGroup.children.length; j++) {
            const trigger = triggerAction.triggerGroup.children[j]
            fileLog.log('[extractUsedTypes] 处理 Trigger', j, 'type:', trigger.type)
            if (trigger.type) {
                types.add(trigger.type)
                fileLog.log('[extractUsedTypes] 添加 Trigger type:', trigger.type)
            } else {
                fileLog.log('[extractUsedTypes] Trigger type 为空', j)
            }
        }

        // 提取 Actions
        fileLog.log('[extractUsedTypes] actionGroup.children 长度:', triggerAction.actionGroup.children.length)
        for (let j = 0; j < triggerAction.actionGroup.children.length; j++) {
            const action = triggerAction.actionGroup.children[j]
            fileLog.log('[extractUsedTypes] 处理 Action', j, 'type:', action.type)
            if (action.type) {
                types.add(action.type)
                fileLog.log('[extractUsedTypes] 添加 Action type:', action.type)
            } else {
                fileLog.log('[extractUsedTypes] Action type 为空', j)
            }
        }
    }

    fileLog.log('[extractUsedTypes] 提取完成, 总类型数:', types.size)
    fileLog.log('[extractUsedTypes] 类型列表:', Array.from(types))
    return types
}

/**
 * 递归收集依赖
 *
 * @param dependencies - 依赖列表
 * @param impSet - 已导入的依赖集合（避免重复）
 * @param impList - 导入代码列表
 * @returns 导入代码列表
 */
function prepareDependencies(
    dependencies: LibPathKey[],
    impSet: Set<LibPathKey> = new Set<LibPathKey>(),
    impList: string[] = []
): string[] {
    fileLog.log('[prepareDependencies] 处理依赖数量:', dependencies.length)

    for (let i = 0; i < dependencies.length; i++) {
        const path = dependencies[i]
        fileLog.log('[prepareDependencies] 处理依赖', i, 'path:', path)

        if (impSet.has(path)) {
            fileLog.log('[prepareDependencies] 依赖已存在,跳过:', path)
            continue
        }

        const file = javaLib[path]
        if (!file) {
            fileLog.log('[prepareDependencies] 错误: 找不到依赖:', path)
            fileLog.log('[prepareDependencies] 可用的 javaLib keys:', Object.keys(javaLib))
            throw new Error(`no path in javaLib, path is ${path}`)
        }

        fileLog.log('[prepareDependencies] 添加依赖:', path)
        impSet.add(path)
        impList.push(`"${path}":${file.content}`)

        if (file.dependencies && file.dependencies.length > 0) {
            fileLog.log('[prepareDependencies] 依赖有子依赖,数量:', file.dependencies.length)
            const list = file.dependencies.map(d => d.path) as LibPathKey[]
            prepareDependencies(list, impSet, impList)
        } else {
            fileLog.log('[prepareDependencies] 依赖无子依赖')
        }
    }

    fileLog.log('[prepareDependencies] 完成, 总依赖数:', impList.length)
    return impList
}

/**
 * 创建 javaLib 对象代码
 *
 * @param dependencies - 依赖列表
 * @returns javaLib 对象的 JavaScript 代码
 *
 * @example
 * const script = createLibScript(['fanfanlo/triggers/time/timeout'])
 * // script: "let javaLib = { ... }"
 */
export function createLibScript(dependencies: LibPathKey[]): string {
    fileLog.log('[createLibScript] 开始创建 javaLib 脚本')
    fileLog.log('[createLibScript] 输入依赖数量:', dependencies.length)

    // 强制包含的核心依赖
    const storePath = 'fanfanlo/javajs/v1/project/store' as LibPathKey
    const createEventDispatcherPath = 'fanfanlo/javajs/v1/event/createEventDispatcher' as LibPathKey
    const createGetterSetterPath = 'fanfanlo/javajs/v1/utils/createGetterSetter' as LibPathKey

    fileLog.log('[createLibScript] 添加强制依赖 - store:', storePath)
    fileLog.log('[createLibScript] 添加强制依赖 - createEventDispatcher:', createEventDispatcherPath)
    fileLog.log('[createLibScript] 添加强制依赖 - createGetterSetter:', createGetterSetterPath)

    // 合并所有依赖（强制依赖 + 项目依赖）
    const allDeps = [
        storePath,
        createEventDispatcherPath,
        createGetterSetterPath,
        ...dependencies
    ]
    fileLog.log('[createLibScript] 总依赖数量(含强制依赖):', allDeps.length)
    fileLog.log('[createLibScript] 强制依赖数量: 3 (store, createEventDispatcher, createGetterSetter)')
    fileLog.log('[createLibScript] 项目依赖数量:', dependencies.length)

    const list = prepareDependencies(allDeps)
    fileLog.log('[createLibScript] 收集到的代码片段数量:', list.length)

    const content = `let javaLib = {
        ${list.join(",")}
    }`

    fileLog.log('[createLibScript] javaLib 脚本创建完成')
    fileLog.log('[createLibScript] 脚本长度:', content.length)

    return content
}
